/** @file orbitInput: which camera move a pointer gesture or a wheel tick makes (pure mouse rules). */
import type { Tool } from '../../state/editorTypes';

/** A pointer drag step (button 0 left, 1 middle, 2 right) or a wheel tick. */
export type CameraGesture =
  | { kind: 'drag'; button: number; alt: boolean; shift: boolean; tool: Tool; dx: number; dy: number }
  | { kind: 'wheel'; deltaY: number };

/** Orbit and tilt in degrees, pan in target pixels, zoom in integer upscale steps. */
export type CameraMove = { orbit: number; tilt: number; pan: [number, number]; zoom: number };

/** Degrees of orbit / tilt per target pixel dragged. */
const ORBIT_PER_PX = 1;
const TILT_PER_PX = 0.5;

/** The camera move with every component zero except the given ones. */
const move = (m: Partial<CameraMove>): CameraMove => ({ orbit: 0, tilt: 0, pan: [0, 0], zoom: 0, ...m });

/**
 * Mouse rules (W5 plan): middle-drag pans; right-drag and Alt+left orbit (and tilt with vertical travel); with the
 * hand tool left-drag orbits and Shift+left tilts; a wheel tick zooms by one integer upscale (up = in). Anything
 * else (a left drag with another tool) is not a camera gesture: null.
 */
export function orbitInput(g: CameraGesture): CameraMove | null {
  if (g.kind === 'wheel') return g.deltaY === 0 ? null : move({ zoom: g.deltaY < 0 ? 1 : -1 });
  if (g.button === 1) return move({ pan: [g.dx, g.dy] });
  if (g.button === 2 || g.alt) return move({ orbit: g.dx * ORBIT_PER_PX, tilt: g.dy * TILT_PER_PX });
  return handDrag(g);
}

/** A plain left drag: the hand tool orbits (Shift tilts); any other tool owns the drag (null). */
function handDrag(g: CameraGesture & { kind: 'drag' }): CameraMove | null {
  if (g.button !== 0 || g.tool !== 'hand') return null;
  return g.shift ? move({ tilt: g.dy * TILT_PER_PX }) : move({ orbit: g.dx * ORBIT_PER_PX });
}
